import type { RootState } from '@store/rootReducer';
import { useSelector } from 'react-redux';

/**
 * Internal-user role helpers for the Control Center.
 *
 * Four internal roles:
 *   - SUPER_ADMIN: assignment + oversight (assign/reassign HRs, user management, dashboards)
 *   - HR_LEAD:     HR's work with the team in view, assigns jobs, reads SEO/Guides/Email campaigns
 *   - HR:          assigned-account work only (their clients, pipelines, interviews)
 *   - MARKETING:   HR's view, read-only on jobs and companies, plus Guides and SEO
 *
 * **This used to be an allowlist of one, inverted.** `isSuperAdmin` was `!isHrRole`, so anyone
 * whose role was not literally "HR" was a Super Admin — including a role nobody had thought about
 * yet. Adding MARKETING under that model would have granted it every route, every gated API and
 * the agreement bucket, silently. So the polarity is reversed: privilege is granted by naming a
 * role, never by failing to match one.
 *
 * The cost of reversing it is that an ABSENT role no longer reads as privileged, and the old
 * behaviour was load-bearing while the profile loads. Guards must therefore gate on the profile
 * having actually arrived (`state.loginUserProfile.status`), not on the role alone — see
 * `RequireRoles`.
 *
 * Source of truth for the current user's role is the login profile record
 * (`state.loginUserProfile.profile.role`), which carries the real internal role.
 */
export const INTERNAL_HR_ROLE = 'HR';
export const SUPER_ADMIN_ROLE = 'SUPER_ADMIN';
export const MARKETING_ROLE = 'MARKETING';
/**
 * HR Lead (owner, 2026-11-03): an HR who also runs the HR team. Everything an HR does, with the
 * team-wide views (follow-ups, Premium Hiring workload, analytics), plus assigning jobs to
 * recruiters on /hr. Read-only on SEO, Guides, the marketing analytics and Email campaigns. No
 * revenue, and none of the Super Admin's administration: users, settings, agreements, money.
 */
export const HR_LEAD_ROLE = 'HR_LEAD';

/**
 * Values that mean Super Admin but are not the canonical enum.
 *
 * `admin` / `ADMIN` were written by hand on the earliest accounts and still sign in as Super Admin
 * (the BFF logs each such sign-in so they can be migrated to SUPER_ADMIN). Mirrors
 * backend/src/helpers/roles.ts.
 *
 * VIEWER is no longer here (2026-10-31). It was the users route's silent create-default, the role an
 * account got when nobody chose one, and it read as Super Admin by accident. The BFF now refuses a
 * VIEWER account at sign-in, as it does one with no role, so in the staff list it shows as a role
 * that grants nothing, which is what a Super Admin needs to see to fix it.
 */
const LEGACY_SUPER_ADMIN_ROLES = ['admin', 'ADMIN'];

/**
 * Every role a user may be assigned, with the copy the admin UI shows for each.
 *
 * The options and the description were hard-coded in three places — the create popup, the
 * workspace, and a binary ternary that described anything not SUPER_ADMIN as an HR. A third role
 * would have been offered in one list, missing from another, and described as HR in the third.
 */
export const ASSIGNABLE_ROLES = [SUPER_ADMIN_ROLE, HR_LEAD_ROLE, INTERNAL_HR_ROLE, MARKETING_ROLE] as const;
export type AssignableRole = (typeof ASSIGNABLE_ROLES)[number];

export const ROLE_OPTIONS: { value: AssignableRole; label: string }[] = [
  { value: SUPER_ADMIN_ROLE, label: 'Super Admin' },
  { value: HR_LEAD_ROLE, label: 'HR Lead (assigns jobs, sees the whole HR team)' },
  { value: INTERNAL_HR_ROLE, label: 'HR (Dedicated Recruiter)' },
  { value: MARKETING_ROLE, label: 'Marketing (view only for jobs and companies)' },
];

/** What a role may do, in the admin UI's words. */
export const describeRole = (role?: string | null): string => {
  if (isMarketingRole(role)) {
    return 'Sees what an HR sees and publishes guides. Cannot create, edit or assign jobs, or create or edit companies.';
  }
  if (isHrLead(role)) {
    return 'Everything an HR does, with the whole HR team in view, and assigns jobs to recruiters. View only on SEO, Guides and Email campaigns. No revenue or administration.';
  }
  if (isHrRole(role)) return 'Assigned accounts only: their own clients, pipelines and interviews.';
  if (isSuperAdmin(role)) {
    return 'Assignment and oversight: assign or reassign HRs, manage users, and see every dashboard.';
  }
  return 'No access: this role is not recognised by the Control Center.';
};

/** Hook: current internal user's role, or undefined while the profile is loading. */
export const useCurrentUserRole = (): string | undefined =>
  useSelector((state: RootState) => state.loginUserProfile.profile?.role);

/**
 * Hook: current internal user's id (`/api/me` returns it as `_id`), or undefined while the
 * profile is loading. Used to pin an HR's views to their own worklist.
 */
export const useCurrentUserId = (): string | undefined =>
  useSelector((state: RootState) => state.loginUserProfile.profile?._id);

/** A stored value that grants Super Admin without being the canonical enum; due for migration. */
export const isLegacyRole = (role?: string | null): boolean =>
  !!role && LEGACY_SUPER_ADMIN_ROLES.includes(role);

/** True when the caller is a dedicated HR. */
export const isHrRole = (role?: string | null): boolean => role === INTERNAL_HR_ROLE;

/** True when the caller is an HR Lead. Not an HR: `isHrRole` stays the plain recruiter. */
export const isHrLead = (role?: string | null): boolean => role === HR_LEAD_ROLE;

/**
 * The recruiting team: HR and HR Lead. What an HR may do, an HR Lead may do too, and both appear
 * in the recruiter pickers, since an HR Lead can take a job herself.
 */
export const isHrTeam = (role?: string | null): boolean => isHrRole(role) || isHrLead(role);

/** True when the caller is Marketing: HR's view of the console, read-only on jobs and companies, plus Guides. */
export const isMarketingRole = (role?: string | null): boolean => role === MARKETING_ROLE;

/**
 * Named, not inferred.
 *
 * Previously `!isHrRole`, which made every unrecognised value privileged. An unknown role now has
 * no access at all, which is the whole point of the change.
 */
export const isSuperAdmin = (role?: string | null): boolean =>
  role === SUPER_ADMIN_ROLE || (!!role && LEGACY_SUPER_ADMIN_ROLES.includes(role));

/**
 * Who may assign a JOB to a recruiter (assignJobHr, and the hiring mode that goes with it): the
 * /hr page and the recruiter controls on the job record. Super Admins and HR Leads. Assigning a
 * dedicated HR to a whole CLIENT (assignHr) stays the Super Admin's; that is not this.
 */
export const canAssignJobs = (role?: string | null): boolean => isSuperAdmin(role) || isHrLead(role);

/** Who may assign a dedicated HR to a client (assignHr) and manage the team itself: Super Admins only. */
export const canManageHrAssignments = (role?: string | null): boolean => isSuperAdmin(role);

/**
 * The team-wide views: every recruiter's follow-ups, the Premium Hiring workload and the
 * recruiter filters. The backend scopes an HR to themselves and answers these two team-wide.
 */
export const hasTeamView = (role?: string | null): boolean => isSuperAdmin(role) || isHrLead(role);

/**
 * Revenue: the Revenue tab and the placement fee card. Not Marketing, which has no clients, and
 * not an HR Lead (owner, 2026-11-03): her views are team-wide, so a revenue figure there would be
 * the whole team's book. An HR keeps "My revenue", scoped to their own clients by the backend.
 */
export const canSeeRevenue = (role?: string | null): boolean => isSuperAdmin(role) || isHrRole(role);

/** SEO, Guides and the marketing analytics, to read. An HR Lead reads them; she does not change them. */
export const canViewMarketing = (role?: string | null): boolean =>
  isSuperAdmin(role) || isMarketingRole(role) || isHrLead(role);

/** SEO and Guides, to change: sync, notes, create, edit, import, publish, delete. */
export const canEditMarketing = (role?: string | null): boolean => isSuperAdmin(role) || isMarketingRole(role);

/** Hook form of canEditMarketing, for the pages that hide their write controls. */
export const useCanEditMarketing = (): boolean => canEditMarketing(useCurrentUserRole());

/** Email campaigns, to read: Super Admins and HR Leads. Every write stays the Super Admin's. */
export const canViewEmailCampaigns = (role?: string | null): boolean => isSuperAdmin(role) || isHrLead(role);

/** Email campaigns, to change. Super Admins only, as the BFF enforces on every non-GET. */
export const canEditEmailCampaigns = (role?: string | null): boolean => isSuperAdmin(role);

/** Hook form of canEditEmailCampaigns. */
export const useCanEditEmailCampaigns = (): boolean => canEditEmailCampaigns(useCurrentUserRole());

/** Guides and SEO: Super Admins and Marketing write them, an HR Lead reads them. */
export const canAccessGuides = (role?: string | null): boolean => canViewMarketing(role);

/** External jobs (employer career-site listings): every staff role. The BFF gate is the same. */
export const canAccessExternalJobs = (role?: string | null): boolean =>
  isSuperAdmin(role) || isHrTeam(role) || isMarketingRole(role);

/** Any role the console recognises. An unknown role sees nothing. */
export const isKnownStaffRole = (role?: string | null): boolean =>
  isSuperAdmin(role) || isHrTeam(role) || isMarketingRole(role);

/**
 * Who may create, edit or assign jobs, and create or edit companies and employer accounts.
 *
 * Everyone but Marketing (owner's brief, 2026-10-19): the marketing team runs sign-ups and
 * promotions and needs to see every job and company, but not to change the supply side. The BFF
 * (MARKETING_REFUSED_FIELDS) and the backend refuse the same operations, so hiding the controls
 * is presentation; the refusal does not depend on it.
 */
export const canWriteJobsAndCompanies = (role?: string | null): boolean =>
  isSuperAdmin(role) || isHrTeam(role);

/** Hook form of canWriteJobsAndCompanies, for the pages that hide their edit controls. */
export const useCanWriteJobsAndCompanies = (): boolean =>
  canWriteJobsAndCompanies(useCurrentUserRole());

/**
 * Where a role lands when it has no business on the page it asked for.
 *
 * Both guards used to redirect to `/dashboard`, which Marketing must not see — so a Marketing user
 * would have been bounced from the dashboard to the dashboard forever.
 */
export const homeRouteFor = (_role?: string | null): string => '/dashboard';

/**
 * Where a role starts the day: the page after sign-in, and what `/` opens.
 *
 * Not `homeRouteFor`, which is where a guard bounces someone from a page they may not see. An HR
 * starts on their managed clients because that list is their work; bouncing them there from a
 * refused admin page as well would move them further from where they were than the dashboard does.
 */
export const landingRouteFor = (role?: string | null): string => {
  if (isHrRole(role)) return '/my-accounts';
  // Job assignment is the part of the day only she does (owner, 2026-11-03).
  if (isHrLead(role)) return '/hr';
  return homeRouteFor(role);
};

/**
 * The page to open once signed in.
 *
 * A deep link the user was sent to /login from wins, so a shared URL still lands where it points.
 * `/` and `/login` are not destinations: `/` is what an unauthenticated visit to the bare domain
 * became, and treating it as a choice would send every HR to the dashboard.
 */
export const postSignInRoute = (role?: string | null, from?: string | null): string => {
  const path = (from ?? '').split(/[?#]/)[0];
  if (!from || path === '/' || path === '/login' || !from.startsWith('/') || from.startsWith('//')) {
    return landingRouteFor(role);
  }
  return from;
};

/**
 * The stored enum as a readable label: SUPER_ADMIN -> "Super Admin".
 *
 * Presentation only — the value itself is never rewritten. Lives here rather
 * than in a component because the sidebar and the phone drawer both show it,
 * and two copies would eventually disagree.
 */
export const formatRole = (role?: string | null): string => {
  if (!role) return 'Not set';
  if (role === INTERNAL_HR_ROLE) return 'HR';
  if (role === HR_LEAD_ROLE) return 'HR Lead';
  // The legacy values grant Super Admin (see LEGACY_SUPER_ADMIN_ROLES). Title-casing them showed
  // "Admin" and "Viewer", and "Viewer" reads as read-only for an account that can do everything.
  if (isLegacyRole(role)) return 'Super Admin (legacy)';
  return role
    .replace(/_/g, ' ')
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
};
